/**
 * CetusEd Community Portal — Root App Component
 *
 * COMPLIANCE: Routes are organized by feature domain. Every route requiring
 *   authentication is wrapped in ProtectedRoute. Login and passwordless
 *   access (magic link / portal code) routes are unauthenticated.
 *
 * MIGRATION TARGET: This is a new application. Route structure mirrors the
 *   community feature set defined in CP-R1324.
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider';
import { ProtectedRoute } from './ui/ProtectedRoute';
import { Layout } from './ui/Layout';
import { LoginPage } from './auth/LoginPage';
import { DocumentList } from './features/documents/DocumentList';
import { DocumentViewer } from './features/documents/DocumentViewer';
import { ConsentForm } from './features/documents/ConsentForm';
import { Inbox } from './features/communication/Inbox';
import { Compose } from './features/communication/Compose';
import { Thread } from './features/communication/Thread';
import { Calendar } from './features/schedule/Calendar';
import { MeetingRequest } from './features/schedule/MeetingRequest';
import { FileUpload } from './features/file-sharing/FileUpload';
import { FileList } from './features/file-sharing/FileList';
import { Profile } from './features/profile/Profile';
import { Notifications } from './features/profile/Notifications';

export const App: React.FC = () => {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<ProtectedRoute />}>
          <Route element={<Layout />}>
            <Route path="/documents" element={<DocumentList />} />
            <Route path="/documents/:documentId" element={<DocumentViewer />} />
            <Route path="/documents/:documentId/consent" element={<ConsentForm />} />
            <Route path="/messages" element={<Inbox />} />
            <Route path="/messages/compose" element={<Compose />} />
            <Route path="/messages/:threadId" element={<Thread />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/calendar/request" element={<MeetingRequest />} />
            <Route path="/files" element={<FileList />} />
            <Route path="/files/upload" element={<FileUpload />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/profile/notifications" element={<Notifications />} />
            <Route path="/" element={<Navigate to="/documents" replace />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </AuthProvider>
  );
};
